import React, { useState, useEffect } from "react";
import {
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSelect,
    StandardModalState,
    UserSelectionMetricCopy,
    UserSelectionMetricDescription,
    UserSelectionMetricSummary,
    UserSelectionMetricTitle,
    UserSelectionMetricValue,
    UserViewerListPreview
} from "src/components";
import getDonationClickVisitors from "../hooks/getDonationClickVisitors";
import { USER_GROUP } from "src/constants/donations";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";

const LinkClicksModal = ({ donationData, showLinkClicksModal, handleCloseLinkClicksModal, user }) => {
    const { donationClickVisitors, isFetchingVisitors } = getDonationClickVisitors(user.token, donationData);
    
    const userTypeDefault = () => {
        if (donationData.displayToEmployees && donationData.displayToClients) {
            return USER_GROUP.EMPLOYEES; //default to employees
        } else if (donationData.displayToEmployees) {
            return USER_GROUP.EMPLOYEES;
        } else if (donationData.displayToClients) {
            return USER_GROUP.CLIENTS;
        }
    };

    const filterVisitorsByUserType = (userType) => {
        return userType === USER_GROUP.EMPLOYEES ? 
            donationClickVisitors.employees : 
            donationClickVisitors.clients;
    };

    const [userType, setUserType] = useState(userTypeDefault());
    const [viewersList, setViewersList] = useState(filterVisitorsByUserType(userTypeDefault()));

    const handleUserTypeChange = (e) => {
        const value = e.target.value;
        setUserType(value);
    };
    
    useEffect(() => {
        const filteredList = filterVisitorsByUserType(userType);
        setViewersList(filteredList);
    }, [userType, isFetchingVisitors]);

    const getTotalLinkClicks = (viewersList) => {
        if (viewersList.length === 0)
            return 0;

        return viewersList.map(employeeDetails => employeeDetails.numberOfClicks).reduce((total, numberOfClicks) => total + numberOfClicks, 0);
    };
    
    const clickedCount = viewersList ? getTotalLinkClicks(viewersList) : 0;
    const viewsCountFormatted = `${parseInt(clickedCount).toLocaleString("en-us")} ${clickedCount > 1 || clickedCount === 0 ? "clicks" : "click"}`;

    return (
        <StandardModal
            open={showLinkClicksModal}
            onClose={handleCloseLinkClicksModal}
            title="Link Clicks"
            widthMode="wide"
            headerAside={!isFetchingVisitors && (
                <div style={{ width: "13rem" }}>
                    <StandardModalSelect value={userType} onChange={handleUserTypeChange}>
                        {donationData.displayToEmployees && <option value={USER_GROUP.EMPLOYEES}>{capitalizeString(pluralize(customNames.employees))}</option>}
                        {donationData.displayToClients && <option value={USER_GROUP.CLIENTS}>{capitalizeString(pluralize(customNames.clients))}</option>}
                    </StandardModalSelect>
                </div>
            )}
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton type="button" onClick={handleCloseLinkClicksModal}>
                        Close
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <UserSelectionMetricSummary>
                    <UserSelectionMetricCopy>
                        <UserSelectionMetricTitle>Donation Engagement Stats</UserSelectionMetricTitle>
                        <UserSelectionMetricDescription>
                            Track donation performance by viewing the number of link clicks each cause has received.
                        </UserSelectionMetricDescription>
                    </UserSelectionMetricCopy>
                    {!isFetchingVisitors && <UserSelectionMetricValue>{viewsCountFormatted}</UserSelectionMetricValue>}
                </UserSelectionMetricSummary>
            </StandardModalSection>

            <StandardModalSection>
                {isFetchingVisitors ? (
                    <StandardModalState minHeight="20rem">
                        <LottieLoadingIndicator />
                    </StandardModalState>
                ) : (
                    <UserViewerListPreview
                        viewersList={viewersList}
                        isReaderTypeEmployee={userType === USER_GROUP.EMPLOYEES}
                        clientSecondaryHeader="Job Title"
                        emptyMessage="No user clicks found"
                    />
                )}
            </StandardModalSection>
        </StandardModal>
    );
};

export default LinkClicksModal;
